import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { adminFixtureDeposits, useAdminFixturesFromQuery } from '../fixtures'
import type { AdminDeposit } from '../types'
import { money } from '../../client/prediction-filter'
import { apiUrl } from '../../lib/api-base'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { ADMIN_TOKEN_KEY } from '../types'
import { getAuthItem } from '../../shared/authStorage'
import {
  eventDescription,
  eventTimestamp,
  formatWhen,
  safeAdminError,
} from '../safe-ui'

function isManualDemo(provider?: string) {
  return String(provider || '').toUpperCase() === 'MANUAL_DEMO'
}

function isUniwire(provider?: string) {
  return String(provider || '').toUpperCase() === 'UNIWIRE'
}

function ProviderBadge({ provider }: { provider?: string }) {
  const p = String(provider || '').toUpperCase()
  if (p === 'MANUAL_DEMO') return <span className="cp-chip">MANUAL_DEMO</span>
  if (p === 'UNIWIRE') return <span className="cp-chip cp-chip--warn">UNIWIRE</span>
  return <span>{provider || '—'}</span>
}

export function AdminDepositsPage() {
  const fixtures = useAdminFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [deposits, setDeposits] = useState<AdminDeposit[]>([])
  // Default All — Uniwire completes as COMPLETED, not PENDING.
  const [status, setStatus] = useState('')
  const [provider, setProvider] = useState('')
  const [search, setSearch] = useState('')
  const [clientId, setClientId] = useState('')
  const [selected, setSelected] = useState<(AdminDeposit & Record<string, unknown>) | null>(null)
  const [timeline, setTimeline] = useState<Array<Record<string, unknown>>>([])
  const [busy, setBusy] = useState(false)
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null)
  const [screenshotBusy, setScreenshotBusy] = useState(false)

  async function load() {
    setLoading(true)
    setError('')
    try {
      if (fixtures) {
        let rows = [...adminFixtureDeposits]
        if (status) rows = rows.filter((d) => d.status === status)
        if (provider) rows = rows.filter((d) => d.provider === provider)
        if (clientId.trim()) {
          const id = Number(clientId)
          rows = rows.filter((d) => d.client_id === id)
        }
        if (search.trim()) {
          const q = search.trim().toLowerCase()
          rows = rows.filter(
            (d) =>
              String(d.id).includes(q) ||
              (d.reference || '').toLowerCase().includes(q) ||
              (d.client_email || '').toLowerCase().includes(q),
          )
        }
        setDeposits(rows)
        return
      }
      const data = await adminApi.listDeposits({
        page: 1,
        pageSize: 50,
        status: status || undefined,
        provider: provider || undefined,
        search: search.trim() || undefined,
        clientId: clientId.trim() ? Number(clientId) : undefined,
      })
      setDeposits(data.deposits || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load deposits'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtures])

  async function openDetail(id: number) {
    setError('')
    setTimeline([])
    if (fixtures) {
      setSelected(adminFixtureDeposits.find((d) => d.id === id) || null)
      setTimeline([])
      return
    }
    try {
      const data = await adminApi.getDeposit(id)
      setSelected({
        ...data.deposit,
        has_screenshot: data.has_screenshot ?? data.deposit.has_screenshot,
      })
      setTimeline(data.timeline || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load deposit'))
    }
  }

  const selectedManual = useMemo(
    () => (selected ? isManualDemo(String(selected.provider || '')) : false),
    [selected],
  )
  const selectedUniwire = useMemo(
    () => (selected ? isUniwire(String(selected.provider || '')) : false),
    [selected],
  )

  async function runApprove(reason: string) {
    if (!selected || fixtures || !selectedManual) return
    setBusy(true)
    setDialog(null)
    setNotice('')
    try {
      await adminApi.approveDeposit(selected.id, reason)
      setNotice(`Deposit #${selected.id} approved.`)
      setSelected(null)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Approve failed'))
    } finally {
      setBusy(false)
    }
  }

  async function runReject(reason: string) {
    if (!selected || fixtures || !selectedManual) return
    setBusy(true)
    setDialog(null)
    try {
      await adminApi.rejectDeposit(selected.id, reason)
      setNotice(`Deposit #${selected.id} rejected.`)
      setSelected(null)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Reject failed'))
    } finally {
      setBusy(false)
    }
  }

  async function refresh() {
    if (!selected || fixtures) return
    setBusy(true)
    try {
      await adminApi.refreshDeposit(selected.id)
      await openDetail(selected.id)
      setNotice('Refresh requested.')
    } catch (err) {
      setError(safeAdminError(err, 'Refresh failed'))
    } finally {
      setBusy(false)
    }
  }

  async function openScreenshot() {
    if (!selected || fixtures) return
    setScreenshotBusy(true)
    setError('')
    try {
      const path = adminApi.depositScreenshotUrl(selected.id)
      const token = getAuthItem(ADMIN_TOKEN_KEY)
      const res = await fetch(apiUrl(path), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Screenshot unavailable')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to open screenshot'))
    } finally {
      setScreenshotBusy(false)
    }
  }

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Deposits" title="Deposit queue">
        <p className="cp-muted">
          Manual Demo approve/reject with confirmation. Uniwire refresh only — no force credit.
        </p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Deposit actions are disabled in preview.
        </StateBlock>
      ) : null}

      <div className="ap-filters">
        <label className="cp-field">
          Search
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="reference, email, id"
          />
        </label>
        <label className="cp-field">
          Client ID
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="optional"
            inputMode="numeric"
          />
        </label>
        <label className="cp-field">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="PENDING">PENDING (Manual Demo)</option>
            <option value="AWAITING_PAYMENT">AWAITING_PAYMENT</option>
            <option value="PAYMENT_PENDING">PAYMENT_PENDING</option>
            <option value="PAYMENT_CONFIRMED">PAYMENT_CONFIRMED</option>
            <option value="PROCESSING_MT5_CREDIT">PROCESSING_MT5_CREDIT</option>
            <option value="COMPLETED">COMPLETED</option>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
            <option value="FAILED">FAILED</option>
            <option value="EXPIRED">EXPIRED</option>
            <option value="INCOMPLETE_PAYMENT">INCOMPLETE_PAYMENT</option>
          </select>
        </label>
        <label className="cp-field">
          Provider
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="">All</option>
            <option value="MANUAL_DEMO">MANUAL_DEMO</option>
            <option value="UNIWIRE">UNIWIRE</option>
          </select>
        </label>
        <Button variant="secondary" onClick={() => void load()}>
          Apply
        </Button>
      </div>

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}
      {notice ? <p className="cp-success">{notice}</p> : null}

      <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <ResponsiveTable
          rows={deposits}
          rowKey={(d) => String(d.id)}
          empty={loading ? 'Loading…' : 'No deposits for this filter.'}
            columns={[
              { key: 'id', header: 'ID', render: (d) => d.id },
              { key: 'ref', header: 'Reference', render: (d) => d.reference || '—' },
              {
                key: 'client',
                header: 'Client',
                render: (d) =>
                  d.client_id ? (
                    <Link to={`/admin/clients/${d.client_id}${q}`}>
                      {d.client_email || d.client_name || `#${d.client_id}`}
                    </Link>
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (d) => `${money(d.amount)} ${d.currency || 'USD'}`,
              },
              { key: 'status', header: 'Status', render: (d) => d.status || '—' },
              {
                key: 'provider',
                header: 'Provider',
                render: (d) => <ProviderBadge provider={d.provider} />,
              },
              {
                key: 'open',
                header: '',
                render: (d) => (
                  <Button variant="secondary" size="sm" onClick={() => void openDetail(d.id)}>
                    Open
                  </Button>
                ),
              },
            ]}
          />
      </div>

      {selected ? (
        <div className="cp-panel">
          <h2>Deposit #{selected.id}</h2>
          <dl className="cp-stat-grid">
            <div>
              <dt>Status</dt>
              <dd>{String(selected.status || '—')}</dd>
            </div>
            <div>
              <dt>Provider</dt>
              <dd>
                <ProviderBadge provider={String(selected.provider || '')} />
              </dd>
            </div>
            <div>
              <dt>Amount</dt>
              <dd>
                {money(selected.amount as string | number)} {String(selected.currency || 'USD')}
              </dd>
            </div>
            <div>
              <dt>Reference</dt>
              <dd>{String(selected.reference || '—')}</dd>
            </div>
            <div>
              <dt>Client</dt>
              <dd>
                {selected.client_id ? (
                  <Link to={`/admin/clients/${selected.client_id}${q}`}>#{String(selected.client_id)}</Link>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt>Account</dt>
              <dd>
                {selected.trading_account_id ? (
                  <Link to={`/admin/trading-accounts/${selected.trading_account_id}${q}`}>
                    #{String(selected.trading_account_id)}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{formatWhen(String(selected.created_at || ''))}</dd>
            </div>
            <div>
              <dt>Completed</dt>
              <dd>{formatWhen(String(selected.completed_at || ''))}</dd>
            </div>
          </dl>

          {selected.has_screenshot ? (
            <p>
              <Button variant="ghost" disabled={screenshotBusy || fixtures} onClick={() => void openScreenshot()}>
                {screenshotBusy ? 'Opening…' : 'View screenshot'}
              </Button>
            </p>
          ) : null}

          {timeline.length > 0 ? (
            <div style={{ marginTop: '1rem' }}>
              <h3>Timeline</h3>
              <div className="cp-card-list">
                {timeline.map((ev, i) => (
                  <article key={i} className="cp-row-card">
                    <div>
                      <strong>{String(ev.event_type || 'Event')}</strong>
                      <span className="cp-muted">{formatWhen(eventTimestamp(ev))}</span>
                    </div>
                    <div className="cp-muted">{eventDescription(ev)}</div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          <div className="cp-platform-actions">
            {selectedManual ? (
              <>
                <Button variant="primary" disabled={busy || fixtures} onClick={() => setDialog('approve')}>
                  Approve Manual Demo
                </Button>
                <Button variant="secondary" disabled={busy || fixtures} onClick={() => setDialog('reject')}>
                  Reject Manual Demo
                </Button>
              </>
            ) : null}
            {selectedUniwire ? (
              <Button variant="ghost" disabled={busy || fixtures} onClick={() => void refresh()}>
                Refresh Uniwire status
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setSelected(null)}>
              Close
            </Button>
          </div>
          {selectedUniwire ? (
            <StateBlock tone="warn" title="No Manual Uniwire Approval">
              Uniwire deposits cannot be force-approved or force-credited from Admin UI. Use provider refresh only.
            </StateBlock>
          ) : (
            <StateBlock tone="info">
              Approve/reject are existing Manual Demo admin deposit controls — not arbitrary balance adjustments.
            </StateBlock>
          )}
        </div>
      ) : null}

      <ConfirmDialog
        open={dialog === 'approve'}
        title="Approve Manual Demo deposit?"
        body={<p>Credits via the existing admin approve API for MANUAL_DEMO only.</p>}
        confirmLabel="Approve"
        busy={busy}
        requireReason
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => void runApprove(reason)}
      />
      <ConfirmDialog
        open={dialog === 'reject'}
        title="Reject Manual Demo deposit?"
        body={<p>Rejects via the existing admin reject API for MANUAL_DEMO only.</p>}
        confirmLabel="Reject"
        danger
        busy={busy}
        requireReason
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => void runReject(reason)}
      />
    </section>
  )
}
